import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiDownload, FiPrinter, FiSearch, FiPackage,
  FiHash, FiShoppingCart, FiDollarSign
} from 'react-icons/fi';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { colors } from '../../theme/colors';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import ItemSelect from '../../components/ItemSelect';
import VendorSelect from '../../components/VendorSelect';
import { formatCurrency, formatNumber, formatDate } from '../../utils/formatters';
import { addPrintBrand } from '../../utils/printBranding';

const CHART_COLORS = [
  '#800020', '#A0153E', '#5D0017', '#C4A484', '#8B7355', '#D4AF37',
  '#4A90D9', '#E67E22', '#2ECC71', '#9B59B6', '#1ABC9C', '#E74C3C',
  '#3498DB', '#F39C12', '#27AE60'
];

const formatAmount = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  return Number(amount).toLocaleString('en-PK', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  });
};

const formatDateStr = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-PK', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const ItemPurchaseAnalysis = () => {
  const navigate = useNavigate();
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();

  // Data states
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);

  // Filter states
  const [selectedFYId, setSelectedFYId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [selectedItem, setSelectedItem] = useState('');
  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [taxFilter, setTaxFilter] = useState('both');

  // UI states
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [sortField, setSortField] = useState('totalAmount');
  const [sortDirection, setSortDirection] = useState('desc');

  // Load financial years
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(fyRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);

      if (activeFinancialYear?.id) {
        setSelectedFYId(activeFinancialYear.id);
      } else if (fys.length > 0) {
        setSelectedFYId(fys[0].id);
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear]);

  // Load categories
  const loadCategories = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const catRef = collection(db, 'companies', activeCompany.id, 'inventoryCategories');
      const q = query(catRef, orderBy('categoryName', 'asc'));
      const snapshot = await getDocs(q);
      setCategories(snapshot.docs.map(d => ({ id: d.id, ...d.data() })).filter(c => c.isDeleted !== true));
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  }, [activeCompany]);

  // Load subcategories
  const loadSubCategories = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const subRef = collection(db, 'companies', activeCompany.id, 'inventorySubCategories');
      const snapshot = await getDocs(subRef);
      setSubCategories(snapshot.docs.map(d => ({ id: d.id, ...d.data() })).filter(s => s.isDeleted !== true));
    } catch (error) {
      console.error('Error loading subcategories:', error);
    }
  }, [activeCompany]);

  // Load inventory items
  const loadInventoryItems = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const itemRef = collection(db, 'companies', activeCompany.id, 'inventoryItems');
      const q = query(itemRef, orderBy('itemName', 'asc'));
      const snapshot = await getDocs(q);
      setInventoryItems(snapshot.docs.map(d => ({ id: d.id, ...d.data() })).filter(i => i.isDeleted !== true));
    } catch (error) {
      console.error('Error loading inventory items:', error);
    }
  }, [activeCompany]);

  // Load vendors
  const loadVendors = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const vendorRef = collection(db, 'companies', activeCompany.id, 'vendors');
      const q = query(vendorRef, orderBy('vendorName', 'asc'));
      const snapshot = await getDocs(q);
      setVendors(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  }, [activeCompany]);

  useEffect(() => {
    loadFinancialYears();
    loadCategories();
    loadSubCategories();
    loadInventoryItems();
    loadVendors();

    // Set default date range
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    setDateFrom(firstDay.toISOString().split('T')[0]);
    setDateTo(today.toISOString().split('T')[0]);
  }, [loadFinancialYears, loadCategories, loadSubCategories, loadInventoryItems, loadVendors]);

  // Filtered subcategories based on selected category
  const filteredSubCategories = useMemo(() => {
    if (!selectedCategory) return subCategories;
    return subCategories.filter(sc => sc.categoryId === selectedCategory);
  }, [subCategories, selectedCategory]);

  // Filtered items based on selected category/subcategory
  const filteredItems = useMemo(() => {
    let items = [...inventoryItems];
    if (selectedCategory) {
      items = items.filter(i => i.categoryId === selectedCategory);
    }
    if (selectedSubCategory) {
      items = items.filter(i => i.subCategoryId === selectedSubCategory);
    }
    return items;
  }, [inventoryItems, selectedCategory, selectedSubCategory]);

  // Load transactions
  const loadTransactions = async () => {
    if (!activeCompany?.id || !selectedFYId) return;

    setLoading(true);
    setHasSearched(true);

    try {
      const transRef = collection(db, 'companies', activeCompany.id, 'transactions');
      const conditions = [
        where('tranTypeCode', 'in', ['PI', 'NPI', 'PR', 'NPR']),
        where('financialYearId', '==', selectedFYId)
      ];

      const q = query(transRef, ...conditions);
      const snapshot = await getDocs(q);

      let allTransactions = snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(t => {
          if (!activeBranch || activeBranch.id === 'all') return true;
          return t.branchId === activeBranch.id;
        });

      // Sort by docDate descending
      allTransactions.sort((a, b) => (b.docDate || '').localeCompare(a.docDate || ''));

      setTransactions(allTransactions);
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Build item lookup maps
  const itemLookup = useMemo(() => {
    const map = {};
    inventoryItems.forEach(item => {
      map[item.id] = item;
    });
    return map;
  }, [inventoryItems]);

  const categoryLookup = useMemo(() => {
    const map = {};
    categories.forEach(cat => {
      map[cat.id] = cat;
    });
    return map;
  }, [categories]);

  // Process transactions into item-level analysis
  const itemAnalysis = useMemo(() => {
    // Filter transactions by status: only Posted
    let filtered = transactions.filter(t => ['Posted', 'Paid', 'Partial'].includes(t.status));

    // Date range filter
    if (dateFrom) {
      filtered = filtered.filter(t => (t.docDate || '') >= dateFrom);
    }
    if (dateTo) {
      filtered = filtered.filter(t => (t.docDate || '') <= dateTo);
    }

    // Vendor filter
    if (selectedVendor) {
      filtered = filtered.filter(t => t.partyId === selectedVendor);
    }

    // Status filter on original transactions (before Posted filter)
    // Already filtered to Posted above for accounting
    if (selectedStatus !== 'All' && selectedStatus !== 'Posted') {
      // If user picks non-Posted, return empty since we only count Posted
      return [];
    }

    // Tax filter
    if (taxFilter === 'tax') {
      filtered = filtered.filter(t => !['NPI', 'NPR'].includes(t.tranTypeCode));
    } else if (taxFilter === 'nonTax') {
      filtered = filtered.filter(t => !['PI', 'PR'].includes(t.tranTypeCode));
    }

    // Group by itemId from transaction lines
    const itemMap = {};

    filtered.forEach(t => {
      const lines = t.lines || [];
      const isPurchaseReturn = ['PR', 'NPR'].includes(t.tranTypeCode);

      lines.forEach(line => {
        const itemId = line.itemId || 'unknown';
        const qty = Number(line.quantity) || 0;
        const amount = Number(line.lineTotal) || Number(line.amount) || 0;

        if (!itemMap[itemId]) {
          // Try to get item details from inventory lookup
          const invItem = itemLookup[itemId];
          const catId = invItem?.categoryId || line.categoryId || '';
          const cat = categoryLookup[catId];

          itemMap[itemId] = {
            itemId,
            itemCode: invItem?.itemCode || line.itemCode || '-',
            itemName: invItem?.itemName || line.itemName || 'Unknown Item',
            categoryId: catId,
            categoryName: cat?.categoryName || line.categoryName || '-',
            subCategoryId: invItem?.subCategoryId || line.subCategoryId || '',
            unitOfMeasure: invItem?.unitOfMeasure || line.unitOfMeasure || line.unit || '-',
            qtyPurchased: 0,
            qtyReturned: 0,
            totalPurchaseAmount: 0,
            totalReturnAmount: 0,
            vendorSet: new Set()
          };
        }

        if (isPurchaseReturn) {
          itemMap[itemId].qtyReturned += qty;
          itemMap[itemId].totalReturnAmount += amount;
        } else {
          itemMap[itemId].qtyPurchased += qty;
          itemMap[itemId].totalPurchaseAmount += amount;
        }

        if (t.partyName) {
          itemMap[itemId].vendorSet.add(t.partyName);
        }
      });
    });

    // Convert to array and calculate derived fields
    let items = Object.values(itemMap).map(item => {
      const netQty = item.qtyPurchased - item.qtyReturned;
      const totalAmount = item.totalPurchaseAmount - item.totalReturnAmount;
      const avgCost = netQty > 0 ? totalAmount / netQty : 0;

      return {
        ...item,
        netQty,
        totalAmount,
        avgCost,
        vendorCount: item.vendorSet.size
      };
    });

    // Category filter
    if (selectedCategory) {
      items = items.filter(i => i.categoryId === selectedCategory);
    }

    // SubCategory filter
    if (selectedSubCategory) {
      items = items.filter(i => i.subCategoryId === selectedSubCategory);
    }

    // Item filter
    if (selectedItem) {
      items = items.filter(i => i.itemId === selectedItem);
    }

    return items;
  }, [transactions, dateFrom, dateTo, selectedVendor, selectedStatus, taxFilter, selectedCategory, selectedSubCategory, selectedItem, itemLookup, categoryLookup]);

  // Calculate % of total for each item
  const itemAnalysisWithPercent = useMemo(() => {
    const grandTotal = itemAnalysis.reduce((sum, item) => sum + Math.abs(item.totalAmount), 0);
    return itemAnalysis.map(item => ({
      ...item,
      percentOfTotal: grandTotal > 0 ? (Math.abs(item.totalAmount) / grandTotal) * 100 : 0
    }));
  }, [itemAnalysis]);

  // Sorted items
  const sortedItems = useMemo(() => {
    const sorted = [...itemAnalysisWithPercent];
    sorted.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (['qtyPurchased', 'qtyReturned', 'netQty', 'avgCost', 'totalAmount', 'percentOfTotal', 'totalPurchaseAmount', 'totalReturnAmount'].includes(sortField)) {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [itemAnalysisWithPercent, sortField, sortDirection]);

  // Summary calculations
  const summary = useMemo(() => {
    const uniqueItems = itemAnalysisWithPercent.length;
    const totalQty = itemAnalysisWithPercent.reduce((sum, i) => sum + i.netQty, 0);
    const totalPurchaseAmount = itemAnalysisWithPercent.reduce((sum, i) => sum + i.totalAmount, 0);
    const avgUnitCost = totalQty > 0 ? totalPurchaseAmount / totalQty : 0;

    return { uniqueItems, totalQty, totalPurchaseAmount, avgUnitCost };
  }, [itemAnalysisWithPercent]);

  // Top 15 items by purchase amount (bar chart)
  const topItemsChartData = useMemo(() => {
    return [...itemAnalysisWithPercent]
      .sort((a, b) => Math.abs(b.totalAmount) - Math.abs(a.totalAmount))
      .slice(0, 15)
      .map(item => ({
        name: item.itemName.length > 20 ? item.itemName.substring(0, 20) + '...' : item.itemName,
        fullName: item.itemName,
        amount: item.totalAmount
      }));
  }, [itemAnalysisWithPercent]);

  // Category distribution (pie chart)
  const categoryChartData = useMemo(() => {
    const catMap = {};
    itemAnalysisWithPercent.forEach(item => {
      const catName = item.categoryName || 'Uncategorized';
      if (!catMap[catName]) {
        catMap[catName] = { name: catName, value: 0 };
      }
      catMap[catName].value += Math.abs(item.totalAmount);
    });
    return Object.values(catMap)
      .filter(c => c.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [itemAnalysisWithPercent]);

  // Table totals
  const tableTotals = useMemo(() => {
    return sortedItems.reduce((totals, item) => {
      totals.qtyPurchased += item.qtyPurchased;
      totals.qtyReturned += item.qtyReturned;
      totals.netQty += item.netQty;
      totals.totalAmount += item.totalAmount;
      return totals;
    }, { qtyPurchased: 0, qtyReturned: 0, netQty: 0, totalAmount: 0 });
  }, [sortedItems]);

  // Sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIndicator = (field) => {
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? ' \u2191' : ' \u2193';
  };

  // CSV Export
  const handleExport = () => {
    if (sortedItems.length === 0) return;

    const selectedFY = financialYears.find(f => f.id === selectedFYId);

    let csv = 'Item Purchase Analysis Report\n';
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''}\n`;
    csv += `Period: ${dateFrom || 'All'} to ${dateTo || 'All'}\n`;
    csv += `Generated: ${new Date().toLocaleString()}\n\n`;
    csv += `Items Purchased,${summary.uniqueItems}\n`;
    csv += `Total Qty,${summary.totalQty}\n`;
    csv += `Total Purchase Amount,${summary.totalPurchaseAmount}\n`;
    csv += `Avg Unit Cost,${summary.avgUnitCost}\n\n`;
    csv += 'Item Code,Item Name,Category,Unit,Qty Purchased,Qty Returned,Net Qty,Avg Cost,Total Amount,% of Total\n';

    sortedItems.forEach(item => {
      csv += `"${item.itemCode}","${(item.itemName || '').replace(/"/g, '""')}",`;
      csv += `"${(item.categoryName || '').replace(/"/g, '""')}","${item.unitOfMeasure}",`;
      csv += `${item.qtyPurchased},${item.qtyReturned},${item.netQty},`;
      csv += `${item.avgCost.toFixed(2)},${item.totalAmount.toFixed(2)},`;
      csv += `${item.percentOfTotal.toFixed(2)}%\n`;
    });

    csv += `\nTotals,,,,${tableTotals.qtyPurchased},${tableTotals.qtyReturned},${tableTotals.netQty},,${tableTotals.totalAmount.toFixed(2)},100%\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Item_Purchase_Analysis_${dateFrom || 'all'}_${dateTo || 'all'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Print
  const handlePrint = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Item Purchase Analysis</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:12px}
        h1{color:#800020;font-size:20px;margin-bottom:5px}
        .meta{color:#666;font-size:11px;margin-bottom:15px}
        .summary-grid{display:flex;gap:15px;margin-bottom:20px;flex-wrap:wrap}
        .summary-box{padding:12px 18px;background:#f9f9f9;border-radius:6px;border-left:3px solid #800020;min-width:120px}
        .summary-label{font-size:10px;color:#666;text-transform:uppercase;margin-bottom:4px}
        .summary-val{font-size:16px;font-weight:600}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th{background:#800020;color:white;padding:7px 10px;text-align:left;font-size:10px;text-transform:uppercase}
        th.right{text-align:right}
        td{padding:6px 10px;border-bottom:1px solid #eee;font-size:11px}
        td.right{text-align:right;font-family:'Consolas',monospace}
        .totals-row{background:#f5f5f5;font-weight:600}
        .totals-row td{border-top:2px solid #800020}
        @media print{body{margin:15px}.summary-grid{page-break-inside:avoid}}
      </style></head><body>
      <h1>Item Purchase Analysis</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'} |
        Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''} |
        Period: ${formatDateStr(dateFrom)} to ${formatDateStr(dateTo)} |
        Items: ${sortedItems.length}
      </div>
      <div class="summary-grid">
        <div class="summary-box"><div class="summary-label">Items Purchased</div><div class="summary-val">${formatNumber(summary.uniqueItems)}</div></div>
        <div class="summary-box"><div class="summary-label">Total Qty</div><div class="summary-val">${formatNumber(summary.totalQty)}</div></div>
        <div class="summary-box"><div class="summary-label">Total Purchase Amount</div><div class="summary-val">${formatAmount(summary.totalPurchaseAmount)}</div></div>
        <div class="summary-box"><div class="summary-label">Avg Unit Cost</div><div class="summary-val">${formatAmount(summary.avgUnitCost)}</div></div>
      </div>
      <table>
        <thead><tr>
          <th>Item Code</th><th>Item Name</th><th>Category</th><th>Unit</th>
          <th class="right">Qty Purchased</th><th class="right">Qty Returned</th>
          <th class="right">Net Qty</th><th class="right">Avg Cost</th>
          <th class="right">Total Amount</th><th class="right">% of Total</th>
        </tr></thead>
        <tbody>
          ${sortedItems.map(item => `
            <tr>
              <td>${item.itemCode}</td>
              <td>${item.itemName}</td>
              <td>${item.categoryName}</td>
              <td>${item.unitOfMeasure}</td>
              <td class="right">${formatNumber(item.qtyPurchased)}</td>
              <td class="right">${formatNumber(item.qtyReturned)}</td>
              <td class="right">${formatNumber(item.netQty)}</td>
              <td class="right">${formatAmount(item.avgCost)}</td>
              <td class="right">${formatAmount(item.totalAmount)}</td>
              <td class="right">${item.percentOfTotal.toFixed(1)}%</td>
            </tr>
          `).join('')}
          <tr class="totals-row">
            <td colspan="4" style="text-align:right">Totals (${sortedItems.length} items)</td>
            <td class="right">${formatNumber(tableTotals.qtyPurchased)}</td>
            <td class="right">${formatNumber(tableTotals.qtyReturned)}</td>
            <td class="right">${formatNumber(tableTotals.netQty)}</td>
            <td class="right">-</td>
            <td class="right">${formatAmount(tableTotals.totalAmount)}</td>
            <td class="right">100%</td>
          </tr>
        </tbody>
      </table>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  // Custom chart tooltip
  const CustomBarTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;
      return (
        <ChartTooltipBox>
          <ChartTooltipLabel>{data?.fullName || label}</ChartTooltipLabel>
          {payload.map((entry, index) => (
            <ChartTooltipRow key={index}>
              <ChartTooltipDot style={{ background: entry.color }} />
              <span>{entry.name}: </span>
              <strong>{formatCurrency(entry.value)}</strong>
            </ChartTooltipRow>
          ))}
        </ChartTooltipBox>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const entry = payload[0];
      const total = categoryChartData.reduce((sum, c) => sum + c.value, 0);
      const pct = total > 0 ? ((entry.value / total) * 100).toFixed(1) : '0.0';
      return (
        <ChartTooltipBox>
          <ChartTooltipLabel>{entry.name}</ChartTooltipLabel>
          <ChartTooltipRow>
            <strong>{formatCurrency(entry.value)}</strong>
            <span style={{ marginLeft: 8, color: colors.text.secondary }}>({pct}%)</span>
          </ChartTooltipRow>
        </ChartTooltipBox>
      );
    }
    return null;
  };

  return (
    <PageLayout pageTitle="Item Purchase Analysis" breadcrumb="Reports / Purchase Reports / Item Purchase Analysis">
      {/* Filters */}
      <FiltersCard>
        <FiltersGrid>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select value={selectedFYId} onChange={e => setSelectedFYId(e.target.value)}>
              <option value="">Select Financial Year</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.yearName || fy.yearCode}
                  {fy.isActive ? ' (Active)' : ''}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Date From</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </FilterGroup>

          <FilterGroup>
            <Label>Date To</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </FilterGroup>

          <FilterGroup>
            <Label>Category</Label>
            <Select value={selectedCategory} onChange={e => { setSelectedCategory(e.target.value); setSelectedSubCategory(''); setSelectedItem(''); }}>
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.categoryCode ? `${cat.categoryCode} - ` : ''}{cat.categoryName}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Sub Category</Label>
            <Select value={selectedSubCategory} onChange={e => { setSelectedSubCategory(e.target.value); setSelectedItem(''); }}>
              <option value="">All Sub Categories</option>
              {filteredSubCategories.map(sc => (
                <option key={sc.id} value={sc.id}>
                  {sc.subCategoryCode ? `${sc.subCategoryCode} - ` : ''}{sc.subCategoryName}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Item</Label>
            <ItemSelect
              items={filteredItems}
              selectedItemId={selectedItem}
              onChange={setSelectedItem}
              allOption={{ value: '', label: 'All Items' }}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Vendor</Label>
            <VendorSelect
              vendors={vendors}
              selectedVendorId={selectedVendor}
              onChange={setSelectedVendor}
              allOption={{ value: '', label: 'All Vendors' }}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Status</Label>
            <Select value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)}>
              <option value="All">All Statuses</option>
              <option value="Posted">Posted</option>
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Tax Filter</Label>
            <Select value={taxFilter} onChange={e => setTaxFilter(e.target.value)}>
              <option value="both">Both (Tax + Non-Tax)</option>
              <option value="tax">Tax Only</option>
              <option value="nonTax">Non-Tax Only</option>
            </Select>
          </FilterGroup>
        </FiltersGrid>

        <ButtonGroup>
          <Button onClick={handleExport} disabled={sortedItems.length === 0}>
            <FiDownload /> Export CSV
          </Button>
          <Button onClick={handlePrint} disabled={sortedItems.length === 0}>
            <FiPrinter /> Print
          </Button>
          <Button $primary onClick={loadTransactions} disabled={loading || !selectedFYId}>
            <FiSearch /> {loading ? 'Loading...' : 'Generate Report'}
          </Button>
        </ButtonGroup>
      </FiltersCard>

      {/* Summary Cards */}
      {hasSearched && !loading && (
        <SummaryCards>
          <SummaryCard>
            <SummaryIcon $color={colors.primary.main}><FiPackage /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Items Purchased</SummaryLabel>
              <SummaryValue>{formatNumber(summary.uniqueItems)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color="#28A745"><FiHash /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Qty</SummaryLabel>
              <SummaryValue style={{ color: '#28A745' }}>{formatNumber(summary.totalQty)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color={colors.chart.secondary}><FiShoppingCart /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Purchase Amount</SummaryLabel>
              <SummaryValue>{formatCurrency(summary.totalPurchaseAmount)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color={colors.chart.accent3}><FiDollarSign /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Avg Unit Cost</SummaryLabel>
              <SummaryValue>{formatCurrency(summary.avgUnitCost, { decimals: 2 })}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>
        </SummaryCards>
      )}

      {/* Data Table */}
      <ContentCard>
        <TableContainer>
          {loading ? (
            <EmptyState>
              <EmptyIcon><FiPackage /></EmptyIcon>
              <EmptyText>Loading purchase data...</EmptyText>
            </EmptyState>
          ) : !hasSearched ? (
            <EmptyState>
              <EmptyIcon><FiPackage /></EmptyIcon>
              <EmptyText>Select filters and click "Generate Report" to view item purchase analysis</EmptyText>
            </EmptyState>
          ) : sortedItems.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiPackage /></EmptyIcon>
              <EmptyText>No purchase data found for the selected criteria</EmptyText>
            </EmptyState>
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th $sortable onClick={() => handleSort('itemCode')}>Item Code{getSortIndicator('itemCode')}</Th>
                  <Th $sortable onClick={() => handleSort('itemName')}>Item Name{getSortIndicator('itemName')}</Th>
                  <Th $sortable onClick={() => handleSort('categoryName')}>Category{getSortIndicator('categoryName')}</Th>
                  <Th $sortable onClick={() => handleSort('unitOfMeasure')}>Unit{getSortIndicator('unitOfMeasure')}</Th>
                  <Th $sortable $right onClick={() => handleSort('qtyPurchased')}>Qty Purchased{getSortIndicator('qtyPurchased')}</Th>
                  <Th $sortable $right onClick={() => handleSort('qtyReturned')}>Qty Returned{getSortIndicator('qtyReturned')}</Th>
                  <Th $sortable $right onClick={() => handleSort('netQty')}>Net Qty{getSortIndicator('netQty')}</Th>
                  <Th $sortable $right onClick={() => handleSort('avgCost')}>Avg Cost{getSortIndicator('avgCost')}</Th>
                  <Th $sortable $right onClick={() => handleSort('totalAmount')}>Total Amount{getSortIndicator('totalAmount')}</Th>
                  <Th $sortable $right onClick={() => handleSort('percentOfTotal')}>% of Total{getSortIndicator('percentOfTotal')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {sortedItems.map((item, index) => (
                  <Tr key={item.itemId || index}>
                    <Td><ItemCode>{item.itemCode}</ItemCode></Td>
                    <Td>
                      <ItemLink onClick={() => navigate('/reports/inventory-ledger', { state: { itemId: item.itemId, itemName: item.itemName } })}>
                        {item.itemName}
                      </ItemLink>
                    </Td>
                    <Td>{item.categoryName}</Td>
                    <Td>{item.unitOfMeasure}</Td>
                    <AmountCell $right>{formatNumber(item.qtyPurchased)}</AmountCell>
                    <AmountCell $right $negative={item.qtyReturned > 0}>
                      {item.qtyReturned > 0 ? `(${formatNumber(item.qtyReturned)})` : formatNumber(item.qtyReturned)}
                    </AmountCell>
                    <AmountCell $right style={{ fontWeight: 600 }}>{formatNumber(item.netQty)}</AmountCell>
                    <AmountCell $right>{formatAmount(item.avgCost)}</AmountCell>
                    <AmountCell $right style={{ fontWeight: 600 }}>{formatAmount(item.totalAmount)}</AmountCell>
                    <AmountCell $right>
                      <PercentBar>
                        <PercentFill style={{ width: `${Math.min(item.percentOfTotal, 100)}%` }} />
                        <PercentText>{item.percentOfTotal.toFixed(1)}%</PercentText>
                      </PercentBar>
                    </AmountCell>
                  </Tr>
                ))}

                {/* Totals Row */}
                <TotalsRow>
                  <Td colSpan={4} style={{ textAlign: 'right', fontWeight: 700 }}>
                    Totals ({sortedItems.length} items)
                  </Td>
                  <AmountCell $right style={{ fontWeight: 700 }}>{formatNumber(tableTotals.qtyPurchased)}</AmountCell>
                  <AmountCell $right style={{ fontWeight: 700 }}>
                    {tableTotals.qtyReturned > 0 ? `(${formatNumber(tableTotals.qtyReturned)})` : formatNumber(tableTotals.qtyReturned)}
                  </AmountCell>
                  <AmountCell $right style={{ fontWeight: 700 }}>{formatNumber(tableTotals.netQty)}</AmountCell>
                  <AmountCell $right style={{ fontWeight: 700 }}>-</AmountCell>
                  <AmountCell $right style={{ fontWeight: 700 }}>{formatAmount(tableTotals.totalAmount)}</AmountCell>
                  <AmountCell $right style={{ fontWeight: 700 }}>100%</AmountCell>
                </TotalsRow>
              </Tbody>
            </Table>
          )}
        </TableContainer>
      </ContentCard>

      {/* Charts */}
      {hasSearched && !loading && itemAnalysisWithPercent.length > 0 && (
        <ChartsRow>
          {/* Top 15 Items by Purchase Amount */}
          <ChartSection>
            <ChartTitle>Top 15 Items by Purchase Amount</ChartTitle>
            {topItemsChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={topItemsChartData} layout="vertical" margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                  <XAxis type="number" tick={{ fontSize: 11 }} stroke={colors.text.secondary} tickFormatter={(v) => formatNumber(v, { compact: true })} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} stroke={colors.text.secondary} width={160} />
                  <Tooltip content={<CustomBarTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="amount" name="Purchase Amount" fill={colors.chart.primary} radius={[0, 4, 4, 0]} barSize={18} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No item data available</ChartEmpty>
            )}
          </ChartSection>

          {/* Category Distribution Pie Chart */}
          <ChartSection>
            <ChartTitle>Purchase by Category</ChartTitle>
            {categoryChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={categoryChartData}
                    cx="50%"
                    cy="50%"
                    outerRadius={120}
                    innerRadius={60}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) => `${name.length > 15 ? name.substring(0, 15) + '...' : name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={{ stroke: colors.text.secondary, strokeWidth: 1 }}
                  >
                    {categoryChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No category data available</ChartEmpty>
            )}
          </ChartSection>
        </ChartsRow>
      )}
    </PageLayout>
  );
};

// ============ STYLED COMPONENTS ============

const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 14px;
  margin-bottom: 16px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const Input = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 9px 18px;
  border: ${props => props.$primary ? 'none' : `1px solid ${colors.neutral[300]}`};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;
  transition: all 0.2s;

  ${props => props.$primary ? `
    background: ${colors.primary.main};
    color: white;
    &:hover { background: ${colors.primary.dark}; }
  ` : `
    background: white;
    color: ${colors.text.primary};
    &:hover { background: ${colors.neutral[100]}; border-color: ${colors.primary.main}; }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SummaryIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.$color || colors.primary.main}15;
  color: ${props => props.$color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const SummaryInfo = styled.div`
  min-width: 0;
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
  white-space: nowrap;
`;

const SummaryValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const ChartSection = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
`;

const ChartTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 16px 0;
`;

const ChartEmpty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${colors.text.tertiary};
  font-size: 14px;
`;

const ChartTooltipBox = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${colors.borderRadius.md};
  padding: 10px 14px;
  box-shadow: ${colors.shadows.md};
`;

const ChartTooltipLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 6px;
`;

const ChartTooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 2px;

  strong {
    color: ${colors.text.primary};
  }
`;

const ChartTooltipDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 12px 14px;
  text-align: ${props => props.$right ? 'right' : 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid ${colors.neutral[200]};
  white-space: nowrap;
  user-select: none;

  ${props => props.$sortable && `
    cursor: pointer;
    transition: color 0.2s;
    &:hover {
      color: ${colors.primary.main};
    }
  `}
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.15s;

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const TotalsRow = styled.tr`
  background: ${colors.neutral[100]};
  border-top: 2px solid ${colors.primary.main};

  td {
    padding: 14px;
  }

  &:hover {
    background: ${colors.neutral[100]};
  }
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: ${props => props.$right ? 'right' : 'left'};
`;

const AmountCell = styled(Td)`
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 13px;
  text-align: right;
  color: ${props => props.$negative ? '#DC3545' : colors.text.primary};
`;

const ItemCode = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background: ${colors.primary.main}10;
  color: ${colors.primary.main};
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
`;

const ItemLink = styled.span`
  cursor: pointer;
  color: #1a73e8;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const PercentBar = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 80px;
  height: 22px;
  background: ${colors.neutral[100]};
  border-radius: 4px;
  overflow: hidden;
`;

const PercentFill = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: ${colors.primary.main}25;
  border-radius: 4px;
  transition: width 0.3s ease;
`;

const PercentText = styled.span`
  position: relative;
  z-index: 1;
  width: 100%;
  text-align: right;
  padding-right: 6px;
  font-size: 12px;
  font-weight: 500;
  color: ${colors.text.primary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin-bottom: 8px;
`;

export default ItemPurchaseAnalysis;
